"use client";

/**
 * The bag's live terms (convex/products/websiteTerms#getBagTerms): each
 * product's free delivery and its add-on pieces as they stand TODAY, keyed by
 * product id — what lib/config/websiteAddOns.ts#bagSummary prices the bag
 * with, the same way the order will. `undefined` while loading (the summary
 * then uses the prices the bag saved, and no delivery is free yet).
 *
 * Catalogue data only: a sale never re-runs it (CLAUDE.md cost rule 3). The
 * shelf of a FREE linked piece is a separate, small query (useLinkedShelf),
 * subscribed only when the bag has one.
 */

import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { isPaidAddOn, type BagTerms } from "@/lib/config/websiteAddOns";

export function useBagTerms(
  items: ReadonlyArray<{ productId: string }> | null | undefined,
): Map<string, BagTerms> | undefined {
  // One stable key per set of products, so a re-render never re-subscribes.
  const key = useMemo(
    () => Array.from(new Set((items || []).map((i) => String(i.productId)))).sort().join(","),
    [items],
  );
  const productIds = useMemo(() => (key ? (key.split(",") as Id<"products">[]) : []), [key]);
  const rows = useQuery(
    api.products.websiteTerms.getBagTerms,
    productIds.length ? { productIds } : "skip",
  );
  return useMemo(() => {
    if (!rows) return undefined;
    return new Map<string, BagTerms>(
      rows.map((r) => [
        String(r.productId),
        {
          freeDelivery: r.freeDelivery,
          addOns: r.addOns.map((a) => ({
            id: a.id,
            name: a.name,
            qty: a.qty,
            price: a.price,
            ...(a.productId ? { productId: String(a.productId) } : {}),
          })),
        },
      ]),
    );
  }, [rows]);
}

/**
 * Units on the shelf of each FREE linked piece in the bag (null = no limit),
 * so the bag says "not included" for one that has run out, as the order will.
 * Nothing is read when the bag has none.
 */
export function useLinkedShelf(terms: ReadonlyMap<string, BagTerms> | undefined): Map<string, number | null> | undefined {
  const key = useMemo(() => {
    const ids = new Set<string>();
    for (const t of terms?.values() ?? []) {
      for (const p of t.addOns) if (p.productId && !isPaidAddOn(p)) ids.add(String(p.productId));
    }
    return Array.from(ids).sort().join(",");
  }, [terms]);
  const productIds = useMemo(() => (key ? (key.split(",") as Id<"products">[]) : []), [key]);
  const rows = useQuery(
    api.products.websiteTerms.getAddOnStock,
    productIds.length ? { productIds } : "skip",
  );
  return useMemo(
    () => (rows ? new Map<string, number | null>(rows.map((r) => [String(r.productId), r.available])) : undefined),
    [rows],
  );
}
